import React, { useEffect, useState } from 'react';
import { TextSection, PhotosSection, ElementsSection, UploadSection, BackgroundSection, LayersSection, SizeSection, SectionTab } from 'polotno/side-panel';
import { cn } from '../../utils/cn';

import { Image as ImageIcon, Shapes, Type, CloudUpload, Loader2 } from 'lucide-react';
import FeedIcon from '../icons/FeedIcon';
import './side-panel.css';
import LayersIcon from '../icons/LayersIcon';
import { fetchProducts, shuffleProducts } from '../../services/productFeed';
import Attribute from '../Attribute';
import DraggableImage from '../DraggableImage';
import { useSelector } from 'react-redux';



const CustomSection = {
    name: 'feed',
    Tab: (props) => (
        <div className='h-[100px] flex items-center justify-center'>
            <SectionTab name="Product Feed" {...props}>
                <div className={cn("p-1 border border-transparent", props.active && 'text-highlight border border-[#E5E5E5] rounded-lg shadow-lg')}>
                    <FeedIcon fill={props.active ? '#2F86DC' : '#4A4A4A'} />
                </div>
            </SectionTab>
        </div>
    ),
    Panel: ({ store }) => {
        const [products, setProducts] = useState([]);
        const [currentProduct, setCurrentProduct] = useState(null);
        const [isLoading, setIsLoading] = useState(true);
        const [error, setError] = useState(null);

        // Fetch products and select random one on mount
        useEffect(() => {
            const fetchAndSelectRandom = async () => {
                try {
                    setIsLoading(true);
                    setError(null);
                    const response = await fetchProducts(1, 50); // Fetch 50 products for good variety
                    
                    if (response.products && response.products.length > 0) {
                        setProducts(response.products);
                        const randomProduct = selectRandomProduct(response.products);
                        setCurrentProduct(randomProduct);
                        // Don't call applyProductToTemplate here - let the useEffect handle it
                    } else {
                        setError('No products found');
                    }
                } catch (err) {
                    console.error('Error fetching products:', err);
                    setError('Failed to load products');
                } finally {
                    setIsLoading(false);
                }
            };

            fetchAndSelectRandom();
        }, []);

        // Apply product to template whenever currentProduct changes
        useEffect(() => {
            if (currentProduct && store) {
                applyProductToTemplate(currentProduct);
            }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [currentProduct]);

        const selectRandomProduct = (productList) => {
            const randomIndex = Math.floor(Math.random() * productList.length);
            return productList[randomIndex];
        };

        const shuffleProduct = async () => {
            try {
                setIsLoading(true);
                const response = await shuffleProducts(1); // Fetch 1 random product
                
                if (response.products && response.products.length > 0) {
                    const newProduct = response.products[0];
                    setCurrentProduct(newProduct);
                }
            } catch (err) {
                console.error('Error shuffling product:', err);
                setError('Failed to shuffle product');
            } finally {
                setIsLoading(false);
            }
        };

        const applyProductToTemplate = (product) => {
            if (!product || !store) return;

            // Deep clone the template JSON to avoid mutation issues
            const json = JSON.parse(JSON.stringify(store.toJSON()));
            
            json.pages.forEach(page => {
                page.children.forEach(element => {
                    if (element.custom?.isVariable) {
                        const varConfig = element.custom;
                        
                        if (varConfig.variableType === 'text') {
                            // Replace text variable
                            const value = getVariableValue(varConfig.variableName, product);
                            element.text = value;
                        } else if (varConfig.variableType === 'image') {
                            // Replace image variable
                            const images = product.images || [];
                            const imageIndex = varConfig.imageIndex || 0;
                            
                            if (images[imageIndex]?.src) {
                                element.src = images[imageIndex].src;
                                element.visible = true;
                            } else {
                                element.visible = false;
                            }
                        }
                    }
                });
            });

            store.loadJSON(json);
        };

        const getVariableValue = (variableName, product) => {
            const formatPrice = (price, currency = 'USD') => {
                if (typeof price !== 'number') return price;
                try {
                    return new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: currency,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    }).format(price);
                } catch (e) {
                    return new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    }).format(price);
                }
            };

            const mapping = {
                'brand': product.vendor || 'N/A',
                'title': product.title || 'Product Title',
                'price': formatPrice(product.min_price || 0, product.currency || 'INR'),
                'sale_price': formatPrice(product.sale_price || product.min_price || 0, product.currency || 'INR'),
                'description': (product.description || '')
                    .replace(/<[^>]*>/g, '')
                    .substring(0, 200)
            };
            
            return mapping[variableName] || '';
        };

        if (isLoading) {
            return (
                <div className='flex flex-col items-center justify-center h-full p-4'>
                    <Loader2 className="w-8 h-8 animate-spin text-[#8E0DFF] mb-3" />
                    <p className='text-sm text-gray-500'>Loading products...</p>
                </div>
            );
        }

        if (error) {
            return (
                <div className='flex flex-col items-center justify-center h-full p-4'>
                    <p className='text-sm text-red-500 mb-3'>{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-[#8E0DFF] hover:bg-[#7A0BE6] text-white rounded-lg text-sm transition-colors"
                    >
                        Retry
                    </button>
                </div>
            );
        }

        if (!currentProduct) {
            return (
                <div className='flex flex-col items-center justify-center h-full p-4'>
                    <p className='text-sm text-gray-500'>No product selected</p>
                </div>
            );
        }

        const productImage = currentProduct.images?.[0]?.src || 'https://via.placeholder.com/300';
        const productTitle = currentProduct.title || 'Product';
        const productPrice = currentProduct.min_price || 0;
        const productCurrency = currentProduct.currency || 'INR';
        const productBrand = currentProduct.vendor || 'Brand';

        const formatPrice = (price, currency) => {
            try {
                return new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: currency,
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                }).format(price);
            } catch (e) {
                return `$${price}`;
            }
        };

        return (
            <div className='flex flex-col h-full'>
                {/* Header */}
                <div className="p-4 border-b">
                    <h3 className='text-sm font-semibold text-[#4A4A4A]'>Current Product</h3>
                    <p className='text-xs text-gray-500 mt-1'>Template applied with product data</p>
                </div>

                {/* Product Card */}
                <div className='flex-1 overflow-y-auto p-4'>
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        {/* Product Image */}
                        <div className="aspect-square w-full bg-gray-100 relative overflow-hidden">
                            <img
                                src={productImage}
                                alt={productTitle}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/300';
                                }}
                            />
                        </div>

                        {/* Product Info */}
                        <div className="p-4 space-y-2">
                            <h4 className="font-semibold text-sm text-gray-900 line-clamp-2">
                                {productTitle}
                            </h4>
                            <p className="text-lg font-bold text-[#8E0DFF]">
                                {formatPrice(productPrice, productCurrency)}
                            </p>
                            <p className="text-xs text-gray-500">
                                {productBrand}
                            </p>
                        </div>
                    </div>
                {/* Product Images */}
                <div className="p-4 border-t border-gray-100">
                    <h3 className="text-sm font-semibold text-[#4A4A4A] mb-3">
                        Product Images
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                        {currentProduct.images?.map((image, index) => (
                            <DraggableImage
                                key={index}
                                src={image.src}
                                index={index}
                                store={store}
                                isTemplateMode={true}
                            />
                        ))}
                        {(!currentProduct.images || currentProduct.images.length === 0) && (
                            <p className="text-xs text-gray-400 col-span-2">No images available for this product</p>
                        )}
                    </div>
                </div>

                {/* Draggable Attributes */}
                <div className="p-4 border-t border-gray-100">
                    <h3 className="text-sm font-semibold text-[#4A4A4A] mb-3">
                        Product Fields
                    </h3>
                    <div className="flex flex-row flex-wrap gap-2">
                        <Attribute 
                            label="Brand" 
                            value={productBrand} 
                            store={store}
                            isTemplateMode={true}
                            variableName="brand"
                        />
                        <Attribute 
                            label="Title" 
                            value={productTitle} 
                            store={store}
                            isTemplateMode={true}
                            variableName="title"
                        />
                        {/* <Attribute 
                            label="Description" 
                            value={currentProduct.description?.replace(/<[^>]*>?/gm, '').substring(0, 100) + '...'} 
                            store={store}
                            isTemplateMode={true}
                            variableName="description"
                        /> */}
                        <Attribute 
                            label="Price" 
                            value={formatPrice(productPrice, productCurrency)} 
                            store={store}
                            isTemplateMode={true}
                            variableName="price"
                        />
                         <Attribute 
                            label="Sale Price" 
                            value={formatPrice(currentProduct.sale_price || productPrice, productCurrency)}
                            store={store} 
                            isTemplateMode={true}
                            variableName="sale_price"
                        />
                    </div>
                </div>
                </div>

                {/* Shuffle Button */}
                <div className="p-4 border-t bg-gray-50">
                    <button
                        onClick={shuffleProduct}
                        disabled={isLoading}
                        className="w-full px-4 py-3 bg-[#8E0DFF] hover:bg-[#7A0BE6] disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                        <svg 
                            className="w-5 h-5" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                        >
                            <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                            />
                        </svg>
                        Shuffle Product
                    </button>
                    {products.length > 1 && (
                        <p className="text-xs text-gray-500 text-center mt-2">
                            {products.length} products available
                        </p>
                    )}
                </div>
            </div>
        );
    }
}

const CustomTextSection = {
    ...TextSection,
    name: 'text',
    Tab: (props) => {
        return (
            <div className='h-[100px] flex items-center justify-center'>
                <SectionTab name="Text" {...props} >
                    <div className={cn("p-1 border border-transparent", props.active && 'text-highlight border border-[#E5E5E5] rounded-lg shadow-lg')}>
                        <Type />
                    </div>
                </SectionTab>
            </div>
        )
    },

}
const CustomElementsSection = {
    ...ElementsSection,
    name: 'elements',
    Tab: (props) => {
        return (
            <div className='h-[100px] flex items-center justify-center'>
                <SectionTab name="Elements" {...props}>
                    <div className={cn("p-1 border border-transparent", props.active && 'text-highlight border border-[#E5E5E5] rounded-lg shadow-lg')}>
                        <Shapes className='w-full' />
                    </div>
                </SectionTab>
            </div>
        )
    }
}
const CustomImageSection = {
    ...PhotosSection,
    name: 'image',
    Tab: (props) => (
        <div className='h-[100px] flex items-center justify-center'>
            <SectionTab name="Image" {...props}>
                <div className={cn("p-1 border border-transparent", props.active && 'text-highlight border border-[#E5E5E5] rounded-lg shadow-lg')}>

                    <ImageIcon className='w-full' />
                </div>
            </SectionTab>
        </div>
    )
}
const CustomUploadSection = {
    ...UploadSection,
    name: 'upload',
    Tab: (props) => (
        <div className='h-[100px] flex items-center justify-center'>
            <SectionTab name="Upload" {...props} className="flex items-center justify-center ">
                <div className={cn("p-1 border border-transparent", props.active && 'text-highlight border border-[#E5E5E5] rounded-lg shadow-lg')}>
                    <CloudUpload className='w-full' />
                </div>
            </SectionTab>
        </div>
    )
}

const CustomLayersSection = {
    ...LayersSection,
    name: 'layers',
    Tab: (props) => (
        <div className='h-[100px] flex items-center justify-center'>
            <SectionTab name="Layers" {...props}>
                <div className={cn("p-1 border border-transparent", props.active && 'text-highlight border border-[#E5E5E5] rounded-lg shadow-lg')}>
                    <LayersIcon fill={props.active ? '#2F86DC' : '#4A4A4A'} />
                </div>
            </SectionTab>
        </div>
    )
}


export const sections = [
    CustomSection,
    CustomTextSection,
    CustomImageSection,
    CustomElementsSection,
    CustomUploadSection,
    BackgroundSection,
    CustomLayersSection,
    SizeSection,
]
